import { useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type { CrawlJob, WithResourceId } from '../../../types/knowledge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import {
  invalidateCrawlRelatedQueries,
  normalizeCrawlAfterStartingSelection,
  upsertCrawlInCache,
} from './knowledgeCrawlMutationCache';

export function useStartKnowledgeCrawlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      url,
      options,
    }: {
      databaseId: string;
      url: string;
      options?: {
        maxDepth?: number | null;
        maxPages?: number | null;
        useStartUrlAsPrefixFilter?: boolean | null;
        allowSubdomains?: boolean | null;
        excludedUrls?: string[] | null;
      };
    }) => knowledgeService.startCrawl(databaseId, url, options),
    onSuccess: (crawl, variables) => {
      upsertCrawlInCache(queryClient, variables.databaseId, crawl);
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId);
    },
  });
}

export function useDeleteKnowledgeCrawlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      crawlId,
    }: {
      databaseId: string;
      crawlId: string;
    }) => knowledgeService.deleteCrawl(databaseId, crawlId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<Array<WithResourceId<CrawlJob>>>(
        knowledgeQueryKeys.crawls(variables.databaseId),
        (current) =>
          current?.filter((crawl) => crawl.id !== variables.crawlId) ?? current,
      );
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId, {
        includeCrawlList: true,
      });
    },
  });
}

export function useRestartKnowledgeCrawlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      crawlId,
    }: {
      databaseId: string;
      crawlId: string;
    }) => knowledgeService.restartCrawl(databaseId, crawlId),
    onSuccess: (crawl, variables) => {
      upsertCrawlInCache(queryClient, variables.databaseId, crawl);
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId);
    },
  });
}

export function useRetryFailedKnowledgeCrawlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      crawl,
      scheduleId,
    }: {
      databaseId: string;
      crawl: WithResourceId<CrawlJob>;
      scheduleId?: string;
    }) =>
      knowledgeService.retryFailedCrawl(databaseId, crawl, {
        scheduleId,
      }),
    onSuccess: (newCrawl, variables) => {
      queryClient.setQueryData<Array<WithResourceId<CrawlJob>>>(
        knowledgeQueryKeys.crawls(variables.databaseId),
        (current) => {
          const withoutOld =
            current?.filter((existing) => existing.id !== variables.crawl.id) ??
            [];
          return [newCrawl, ...withoutOld];
        },
      );
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId, {
        includeCrawlList: true,
      });
    },
  });
}

export function useResumeAbandonedKnowledgeCrawlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      crawlId,
    }: {
      databaseId: string;
      crawlId: string;
    }) => knowledgeService.resumeAbandonedCrawl(databaseId, crawlId),
    onSuccess: (crawl, variables) => {
      queryClient.setQueryData<Array<WithResourceId<CrawlJob>>>(
        knowledgeQueryKeys.crawls(variables.databaseId),
        (current) => {
          if (!current) {
            return current;
          }

          return current.map((existing) =>
            existing.id === crawl.id ? crawl : existing,
          );
        },
      );
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId);
    },
  });
}

export function useRunKnowledgeCrawlWithSelectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      databaseId,
      crawlId,
      selectedNodeIds,
    }: {
      databaseId: string;
      crawlId: string;
      selectedNodeIds: string[];
    }) => {
      await knowledgeService.updateCrawlSelection(
        databaseId,
        crawlId,
        selectedNodeIds,
      );
      return knowledgeService.startSelectedUrlCrawl(databaseId, crawlId);
    },
    onSuccess: (crawl, variables) => {
      upsertCrawlInCache(
        queryClient,
        variables.databaseId,
        normalizeCrawlAfterStartingSelection(crawl),
      );
      invalidateCrawlRelatedQueries(queryClient, variables.databaseId);
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.crawlUrlNodes(
          variables.databaseId,
          variables.crawlId,
        ),
      });
    },
  });
}

export function useUpdateKnowledgeCrawlSelectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      crawlId,
      selectedNodeIds,
    }: {
      databaseId: string;
      crawlId: string;
      selectedNodeIds: string[];
    }) =>
      knowledgeService.updateCrawlSelection(
        databaseId,
        crawlId,
        selectedNodeIds,
      ),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.crawls(variables.databaseId),
      });
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.crawlUrlNodes(
          variables.databaseId,
          variables.crawlId,
        ),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}
